import { useEffect, useRef, useState } from "react";

type CameraCaptureProps = {
  onCapture: (file: File) => void;
  onClose: () => void;
};

const CAMERA_ERRORS: Record<string, string> = {
  NotAllowedError:
    "Accès à la caméra refusé. Autorisez la caméra dans les réglages de votre navigateur puis réessayez.",
  NotFoundError: "Aucune caméra détectée sur cet appareil.",
  NotReadableError: "La caméra est déjà utilisée par une autre application.",
};

const DEVICE_STORAGE_KEY = "camera_device_id";

const readSavedDevice = () => {
  try {
    return localStorage.getItem(DEVICE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const saveDevice = (deviceId: string) => {
  try {
    localStorage.setItem(DEVICE_STORAGE_KEY, deviceId);
  } catch {}
};

const videoConstraints = (deviceId: string): MediaTrackConstraints => ({
  ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: "environment" } }),
  width: { ideal: 4096 },
  height: { ideal: 2160 },
});

function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState(readSavedDevice);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Votre navigateur ne permet pas d'accéder à la caméra.");
        return;
      }
      setIsReady(false);
      setError(null);
      stopStream();
      try {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints(deviceId),
            audio: false,
          });
        } catch (err) {
          if (!deviceId || (err as DOMException)?.name !== "OverconstrainedError") throw err;
          setDeviceId("");
          return;
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        window.umami?.track("camera_opened");

        const all = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        setDevices(all.filter((d) => d.kind === "videoinput"));
        const activeId = stream.getVideoTracks()[0]?.getSettings().deviceId;
        if (activeId && activeId !== deviceId) setDeviceId(activeId);
      } catch (err) {
        const name = (err as DOMException)?.name || "unknown";
        window.umami?.track("camera_error", { reason: name });
        setError(CAMERA_ERRORS[name] || "Impossible d'ouvrir la caméra.");
      }
    };

    start();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [deviceId]);

  const selectDevice = (id: string) => {
    saveDevice(id);
    setDeviceId(id);
  };

  const takePicture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError("La capture a échoué, réessayez.");
          return;
        }
        const now = Date.now();
        onCapture(
          new File([blob], `camera-${now}.jpg`, {
            type: "image/jpeg",
            lastModified: now,
          })
        );
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <div className="camera-overlay">
      <video
        ref={videoRef}
        className="camera-video"
        autoPlay
        playsInline
        muted
        onLoadedMetadata={() => setIsReady(true)}
      />
      {devices.length > 1 ? (
        <select
          className="camera-select"
          aria-label="Choisir la caméra"
          value={deviceId}
          onChange={(e) => selectDevice(e.target.value)}
        >
          {devices.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Caméra ${i + 1}`}
            </option>
          ))}
        </select>
      ) : null}
      {error ? <p className="camera-error">{error}</p> : null}
      <div className="camera-controls">
        <button type="button" className="camera-cancel" onClick={onClose}>
          Annuler
        </button>
        <button
          type="button"
          className="camera-shutter"
          aria-label="Prendre la photo"
          disabled={!isReady || !!error}
          onClick={takePicture}
        />
        <span className="camera-spacer" />
      </div>
    </div>
  );
}

export default CameraCapture;
